"use client";

import type { LucideIcon } from "lucide-react";
import { Mail } from "lucide-react";

export interface Step {
  number: number;
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
}

interface StepsDisplayProps {
  steps: Step[];
}

export function StepsDisplay({ steps }: StepsDisplayProps) {
  return (
    <div className="bg-zinc-50 rounded-xl p-6 md:p-8">
      <h4 className="text-xl font-semibold text-primary mb-8">So einfach geht&apos;s</h4>

      {/* Desktop: Horizontal layout with connecting lines */}
      <div className="hidden md:block relative">
        {/* Connector line behind circles */}
        <div className="absolute top-6 left-[calc(16.67%+24px)] right-[calc(16.67%+24px)] h-0.5 bg-primary/30" />

        <div className="grid grid-cols-3 gap-6 relative">
          {steps.map((step, index) => (
            <div key={index} className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center text-xl font-bold mb-4 relative z-10 ring-4 ring-zinc-50">
                {step.icon ? <step.icon className="w-6 h-6" /> : step.number}
              </div>
              <p className="text-base text-zinc-700">{step.title}</p>
              {step.subtitle && (
                <p className="text-base font-semibold text-zinc-800 mt-1">{step.subtitle}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: Vertical layout with connecting lines */}
      <div className="md:hidden space-y-0">
        {steps.map((step, index) => (
          <div key={index} className="relative flex items-start gap-4">
            {/* Vertical connector line */}
            {index < steps.length - 1 && (
              <div className="absolute left-6 top-12 w-0.5 h-[calc(100%-24px)] bg-primary/30 -translate-x-1/2" />
            )}

            {/* Step circle */}
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center text-xl font-bold relative z-10 ring-4 ring-zinc-50">
              {step.icon ? <step.icon className="w-6 h-6" /> : step.number}
            </div>

            {/* Content */}
            <div className="flex-1 pb-6">
              <p className="text-base text-zinc-700">{step.title}</p>
              {step.subtitle && (
                <p className="text-base font-semibold text-zinc-800 mt-1">{step.subtitle}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Predefined step configurations for different sections
export const oekopunkteSteps: Step[] = [
  {
    number: 1,
    title: "Registrieren Sie sich zunächst auf unserer Seite",
  },
  {
    number: 2,
    title: "Geben Sie kostenfrei an, ob Sie Ökopunkte kaufen oder verkaufen möchten",
  },
  {
    number: 3,
    title: "Erhalten Sie automatisch Angebote per",
    subtitle: "E-Mail",
    icon: Mail,
  },
];

export const landverpachtungSteps: Step[] = [
  {
    number: 1,
    title: "Sie registrieren sich und geben uns die Daten Ihrer Flächen auf",
  },
  {
    number: 2,
    title: "Wir überprüfen Ihre Flächen und geben Ihnen binnen 4 Wochen eine Potenzialanalyse",
  },
  {
    number: 3,
    title: "Sie erhalten von uns ein",
    subtitle: "Pachtangebot",
    icon: Mail,
  },
];

export const jagdpachtSteps: Step[] = [
  {
    number: 1,
    title: "Sie registrieren sich unverbindlich und kostenfrei auf unserer Seite",
  },
  {
    number: 2,
    title: "Teilen Sie uns Ihr Angebot oder Ihr Gesuch mit",
  },
  {
    number: 3,
    title: "Sie erhalten von uns ein",
    subtitle: "Pachtangebot per E-Mail",
    icon: Mail,
  },
];
